import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { TypographyLarge, TypographySmall } from "../../customComponent";
import { inputOnlyNumbers } from "../../../helpers/inputProcessing";
import parser from "html-react-parser";
import { parseVariables } from "../../../helpers/localize";
import { logger } from "../../../helpers/logger";
import { useSettings } from "../../../modules/SettingsContext";

const assetPerLayerLimit = process.env.REACT_APP_ASSET_PER_LAYER_LIMIT || 50;

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const AssetDuplicateDialog = ({
  open,
  handleClose,
  container,
  asset,
  submit,
}) => {
  const { layerMedias } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const { settings } = useSettings();
  const savedSettings = settings?.assetDuplicate;

  const [optionCount, setOptionCount] = useState(1);
  const [optionPlace, setOptionPlace] = useState("direct");

  const [countMaxReached, setCountMaxReached] = useState(0);

  useEffect(() => {
    if (open) {
      logger.log(`AssetDuplicateDialog opened!`, { asset, settings });
      if (savedSettings) {
        handleCloseDialog();

        const options = { ...savedSettings, count: 1 };
        switch (savedSettings.placement) {
          default:
          case "direct":
            const assetIndex = layerMedias[container.id].findIndex(
              (media) => media.id === asset.id
            );
            options.index = assetIndex + 1;
            break;

          case "top":
            options.index = 0;
            break;

          case "bottom":
            options.index = layerMedias[container.id].length;
            break;
        }

        submit(options);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseDialog = () => {
    setOptionCount(1);
    setOptionPlace("direct");
    handleClose();
  };

  const submitDialog = () => {
    if (!layerMedias[container.id]) {
      return;
    }

    const options = { count: optionCount, placement: optionPlace };
    switch (optionPlace) {
      default:
      case "direct":
        const assetIndex = layerMedias[container.id].findIndex(
          (media) => media.id === asset.id
        );
        options.index = assetIndex + 1;
        break;

      case "top":
        options.index = 0;
        break;

      case "bottom":
        options.index = layerMedias[container.id].length;
        break;
    }

    submit(options);
    handleCloseDialog();
  };

  const handleOptionCount = (e) => {
    if (!layerMedias[container.id]) {
      return;
    }

    let value = e.target.value;
    if (value < 1) {
      value = 1;
    }

    const maxCount = assetPerLayerLimit - layerMedias[container.id].length;
    if (value > maxCount) {
      value = maxCount;
      setCountMaxReached(maxCount);
    } else {
      setCountMaxReached(0);
    }

    setOptionCount(value);
  };

  const previewElement = useMemo(() => {
    if (!asset) {
      return;
    }

    const mediaSrc = asset.url ?? asset.vidSrc ?? asset.src ?? asset.html;
    switch (asset?.type) {
      case "VIDEO":
        return (
          <video
            is="x-muted"
            draggable={false}
            src={mediaSrc}
            alt={asset.name}
            controls={true}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              minHeight: "100%",
              objectFit: "contain",
            }}
          />
        );

      case "IMAGE":
        return (
          <img
            draggable={false}
            src={mediaSrc}
            alt={t("contentImageAltText")}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              objectFit: "contain",
            }}
          />
        );

      case "HTML":
        return (
          <iframe
            draggable={false}
            title={`HTML DUPLICATE`}
            srcDoc={mediaSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              objectFit: "contain",
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
            }}
          />
        );

      default:
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => handleCloseDialog()}
    >
      {/* TODO: Change to "Do you want to save your changes?" */}
      {/* <CloseConfirmation
        open={openCloseConfirmation}
        closeDialog={closeCloseConfirmation}
        handleConfirm={handleCloseConfirmation}
      /> */}

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {t("duplicateDialog.title")}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 3, px: 3 }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            sx={{ p: 4, width: "100%" }}
          >
            <Box
              display={"flex"}
              justifyContent={"left"}
              alignItems={"center"}
              sx={{
                py: 2,
                px: 4,
                width: "100%",
                border: "1px solid black",
                borderRadius: "22px",
              }}
            >
              <Box
                width={"50%"}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
              >
                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("duplicateDialog.countLabel")}
                    </TypographyLarge>
                  </Box>

                  <Box
                    width={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      open={countMaxReached > 0}
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {parseVariables(
                            t("duplicateDialog.countMaxReachedTip"),
                            {
                              maxCount: countMaxReached,
                            }
                          )}
                        </TypographySmall>
                      }
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={optionCount}
                        onChange={(e) => handleOptionCount(e)}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        sx={{ pl: 2 }}
                        InputProps={{ sx: { width: "260px" } }}
                      />
                    </Tooltip>
                  </Box>
                </StyledBox>

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("duplicateDialog.optionPlaceLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("duplicateDialog.optionPlaceTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box width={"100%"} display={"flex"} alignItems={"center"}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      sx={{ pl: 2 }}
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        select
                        SelectProps={{
                          native: true,
                        }}
                        value={optionPlace}
                        onChange={(e) => setOptionPlace(e.target.value)}
                        sx={{ my: 1, width: "260px" }}
                      >
                        <option value={"direct"} key={"direct"}>
                          {t("duplicateDialog.optionPlaceDirect")}
                        </option>
                        <option value={"top"} key={"top"}>
                          {t("duplicateDialog.optionPlaceTop")}
                        </option>
                        <option value={"bottom"} key={"bottom"}>
                          {t("duplicateDialog.optionPlaceBottom")}
                        </option>
                      </TextField>
                      <TypographySmall flex={"none"}>
                        {parser(
                          t(`duplicateDialog.${optionPlace}OptionPlaceTip`)
                        )}
                      </TypographySmall>
                    </Box>
                  </Box>
                </StyledBox>
              </Box>

              <Box width={"50%"} display={"flex"} flexDirection={"column"}>
                <Box width={"100%"} sx={{ py: 1, px: 2 }}>
                  <TypographyLarge sx={{ mb: 1, ml: 1 }}>
                    {t("duplicateDialog.assetPreviewLabel")}
                  </TypographyLarge>
                  <Box
                    display={"flex"}
                    alignContent={"center"}
                    sx={{
                      width: "300px",
                      height: "250px",
                      backgroundColor: "black",
                    }}
                  >
                    {previewElement}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={() => handleCloseDialog()}>
            {tCommon("cancelBtn")}
          </Button>
          <Button variant="contained" onClick={() => submitDialog()}>
            {t("duplicateDialog.submitBtn")}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default AssetDuplicateDialog;
